import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/stores';
import { config } from '@/lib/config';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ListChecks, Plus, Pencil, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import type { DeliveryTargetTypeNode } from '@core';

interface TypeFormState {
  name: string;
  sort_order: string;
  is_active: boolean;
}

interface ItemFormState {
  name: string;
  sort_order: string;
  is_active: boolean;
}

const emptyTypeForm = (): TypeFormState => ({ name: '', sort_order: '0', is_active: true });
const emptyItemForm = (): ItemFormState => ({ name: '', sort_order: '0', is_active: true });

const DeliveryTargetList: React.FC = observer(() => {
  const { tenantStore } = useStores();
  const [types, setTypes] = useState<DeliveryTargetTypeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingTypeId, setEditingTypeId] = useState('');
  const [typeForm, setTypeForm] = useState<TypeFormState>(emptyTypeForm());
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState('');
  const [itemParentType, setItemParentType] = useState<DeliveryTargetTypeNode | null>(null);
  const [itemForm, setItemForm] = useState<ItemFormState>(emptyItemForm());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState('');

  const tenantId = tenantStore.selectedTenant?.id;

  const fetchTypes = useCallback(async () => {
    if (!tenantId) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantId}/delivery-targets`,
        {
          credentials: 'include',
        }
      );

      if (response.ok) {
        setTypes((await response.json()) || []);
      } else {
        toast.error('配信対象の取得に失敗しました');
      }
    } catch (error) {
      console.error('Failed to fetch delivery targets:', error);
      toast.error('配信対象の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const openCreateTypeDialog = () => {
    setEditingTypeId('');
    setTypeForm(emptyTypeForm());
    setTypeDialogOpen(true);
  };

  const openEditTypeDialog = (type: DeliveryTargetTypeNode) => {
    setEditingTypeId(type.id);
    setTypeForm({
      name: type.name,
      sort_order: String(type.sort_order),
      is_active: type.is_active,
    });
    setTypeDialogOpen(true);
  };

  const openCreateItemDialog = (type: DeliveryTargetTypeNode) => {
    setEditingItemId('');
    setItemParentType(type);
    setItemForm(emptyItemForm());
    setItemDialogOpen(true);
  };

  const openEditItemDialog = (type: DeliveryTargetTypeNode, itemId: string) => {
    const item = type.items.find((i) => i.id === itemId);
    if (!item) return;

    setEditingItemId(item.id);
    setItemParentType(type);
    setItemForm({
      name: item.name,
      sort_order: String(item.sort_order),
      is_active: item.is_active,
    });
    setItemDialogOpen(true);
  };

  const validateForm = (name: string): boolean => {
    if (!name.trim()) {
      toast.error('表示名は必須です');
      return false;
    }
    return true;
  };

  const handleSaveType = async () => {
    if (!tenantId || !validateForm(typeForm.name)) return;

    setSaving(true);
    try {
      const url = editingTypeId
        ? `${config.apiBaseUrl}/v1/tenants/${tenantId}/delivery-targets/types/${editingTypeId}`
        : `${config.apiBaseUrl}/v1/tenants/${tenantId}/delivery-targets/types`;

      const response = await fetch(url, {
        method: editingTypeId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: typeForm.name.trim(),
          sort_order: Number(typeForm.sort_order) || 0,
          is_active: typeForm.is_active,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        toast.error(error.error || (editingTypeId ? '更新に失敗しました' : '作成に失敗しました'));
        return;
      }

      toast.success(editingTypeId ? '種別を更新しました' : '種別を作成しました');
      setTypeDialogOpen(false);
      await fetchTypes();
    } catch (error) {
      console.error('Failed to save delivery target type:', error);
      toast.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveItem = async () => {
    if (!tenantId || !itemParentType || !validateForm(itemForm.name)) return;

    setSaving(true);
    try {
      const url = editingItemId
        ? `${config.apiBaseUrl}/v1/tenants/${tenantId}/delivery-targets/items/${editingItemId}`
        : `${config.apiBaseUrl}/v1/tenants/${tenantId}/delivery-targets/items`;

      const response = await fetch(url, {
        method: editingItemId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          type_id: itemParentType.id,
          name: itemForm.name.trim(),
          sort_order: Number(itemForm.sort_order) || 0,
          is_active: itemForm.is_active,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        toast.error(error.error || (editingItemId ? '更新に失敗しました' : '作成に失敗しました'));
        return;
      }

      toast.success(editingItemId ? '項目を更新しました' : '項目を作成しました');
      setItemDialogOpen(false);
      await fetchTypes();
    } catch (error) {
      console.error('Failed to save delivery target item:', error);
      toast.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleTypeActive = async (type: DeliveryTargetTypeNode) => {
    if (!tenantId) return;

    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantId}/delivery-targets/types/${type.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            name: type.name,
            sort_order: type.sort_order,
            is_active: !type.is_active,
          }),
        }
      );

      if (!response.ok) {
        toast.error('ステータスの変更に失敗しました');
        return;
      }

      toast.success(type.is_active ? '無効にしました' : '有効にしました');
      await fetchTypes();
    } catch (error) {
      console.error('Failed to toggle delivery target type:', error);
      toast.error('ステータスの変更に失敗しました');
    }
  };

  const handleToggleItemActive = async (item: DeliveryTargetTypeNode['items'][number]) => {
    if (!tenantId) return;

    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantId}/delivery-targets/items/${item.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            type_id: item.type_id,
            name: item.name,
            sort_order: item.sort_order,
            is_active: !item.is_active,
          }),
        }
      );

      if (!response.ok) {
        toast.error('ステータスの変更に失敗しました');
        return;
      }

      toast.success(item.is_active ? '無効にしました' : '有効にしました');
      await fetchTypes();
    } catch (error) {
      console.error('Failed to toggle delivery target item:', error);
      toast.error('ステータスの変更に失敗しました');
    }
  };

  const handleDeleteType = async (type: DeliveryTargetTypeNode) => {
    if (!tenantId) return;
    if (
      !window.confirm(
        `種別「${type.name}」と配下の項目 ${type.items.length} 件、およびゲストの選択履歴を削除します。よろしいですか?`
      )
    ) {
      return;
    }

    setDeletingId(type.id);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantId}/delivery-targets/types/${type.id}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!response.ok && response.status !== 204) {
        toast.error('削除に失敗しました');
        return;
      }

      toast.success('種別を削除しました');
      await fetchTypes();
    } catch (error) {
      console.error('Failed to delete delivery target type:', error);
      toast.error('削除に失敗しました');
    } finally {
      setDeletingId('');
    }
  };

  const handleDeleteItem = async (item: DeliveryTargetTypeNode['items'][number]) => {
    if (!tenantId) return;
    if (!window.confirm(`項目「${item.name}」とゲストの選択履歴を削除します。よろしいですか?`)) {
      return;
    }

    setDeletingId(item.id);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantId}/delivery-targets/items/${item.id}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!response.ok && response.status !== 204) {
        toast.error('削除に失敗しました');
        return;
      }

      toast.success('項目を削除しました');
      await fetchTypes();
    } catch (error) {
      console.error('Failed to delete delivery target item:', error);
      toast.error('削除に失敗しました');
    } finally {
      setDeletingId('');
    }
  };

  const renderActiveBadge = (isActive: boolean) => (
    <Badge
      variant={isActive ? 'default' : 'secondary'}
      className={isActive ? 'bg-green-100 text-green-800 hover:bg-green-100' : ''}
    >
      {isActive ? '表示中' : '非表示'}
    </Badge>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2">
            <ListChecks className="h-5 w-5" />
            配信対象設定
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchTypes} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              更新
            </Button>
            <Button size="sm" onClick={openCreateTypeDialog}>
              <Plus className="h-4 w-4 mr-1" />
              種別を追加
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            LINE の「配信対象設定」LIFF でゲストが選択する種別と項目を管理します。
            表示名は ID で紐づいているため、後からリネームしてもゲストの選択は維持されます。
          </p>

          {types.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              種別がありません。「種別を追加」から追加してください。
            </div>
          )}

          {types.map((type) => (
            <div key={type.id} className="border rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{type.name}</span>
                  {renderActiveBadge(type.is_active)}
                  <Badge variant="outline">表示順: {type.sort_order}</Badge>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditTypeDialog(type)}
                    aria-label="種別を編集"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleToggleTypeActive(type)}>
                    {type.is_active ? '非表示にする' : '表示する'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteType(type)}
                    disabled={deletingId === type.id}
                    aria-label="種別を削除"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>項目名</TableHead>
                    <TableHead>ステータス</TableHead>
                    <TableHead>表示順</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {type.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell>{renderActiveBadge(item.is_active)}</TableCell>
                      <TableCell>{item.sort_order}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditItemDialog(type, item.id)}
                            aria-label="項目を編集"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleToggleItemActive(item)}>
                            {item.is_active ? '非表示にする' : '表示する'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteItem(item)}
                            disabled={deletingId === item.id}
                            aria-label="項目を削除"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {type.items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                        項目がありません。「項目を追加」から追加してください。
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <div className="mt-3">
                <Button variant="outline" size="sm" onClick={() => openCreateItemDialog(type)}>
                  <Plus className="h-4 w-4 mr-1" />
                  項目を追加
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTypeId ? '種別編集' : '種別を追加'}</DialogTitle>
            <DialogDescription>
              種別は LIFF 画面のセクション見出しとして表示されます。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="delivery-target-type-name">表示名 *</Label>
              <Input
                id="delivery-target-type-name"
                value={typeForm.name}
                onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                placeholder="例: お知らせ"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="delivery-target-type-sort">表示順</Label>
              <Input
                id="delivery-target-type-sort"
                type="number"
                value={typeForm.sort_order}
                onChange={(e) => setTypeForm({ ...typeForm, sort_order: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeDialogOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleSaveType} disabled={saving}>
              {saving ? '保存中...' : editingTypeId ? '更新する' : '作成する'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItemId ? '項目編集' : '項目を追加'}</DialogTitle>
            <DialogDescription>
              「{itemParentType?.name}」の項目です。LIFF ではチェックボックス (Switch) として表示されます。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="delivery-target-item-name">表示名 *</Label>
              <Input
                id="delivery-target-item-name"
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                placeholder="例: メンテナンス情報"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="delivery-target-item-sort">表示順</Label>
              <Input
                id="delivery-target-item-sort"
                type="number"
                value={itemForm.sort_order}
                onChange={(e) => setItemForm({ ...itemForm, sort_order: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setItemDialogOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleSaveItem} disabled={saving}>
              {saving ? '保存中...' : editingItemId ? '更新する' : '作成する'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default DeliveryTargetList;
